<template>
  <div class="whitelist">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" #icon>
      <svg class="icon" aria-hidden="true">
        <use xlink:href="#icon-a-shezhi2"></use>
      </svg>
    </breadcrumbs>
    <NoticeTool />
    <a-spin :spinning="pageLoading">
      <div class="content-wrapper">
      
        <div class="toolbar">
          <div class="status-row">
            <span class="status-label">
              {{ st('settingsWhitelist.statusLabel') }}:
              <span class="status-value">{{ statusText }}</span>
            </span>
            <a-switch
              :checked="whitelistEnabled"
              :loading="statusLoading"
              @change="onStatusChange"
            />
            <artmisads-button type="primary" @click="handleCopy">
              {{ st('settingsWhitelist.btnCopyToOtherMarketplaces') }}
            </artmisads-button>
          </div>
          <artmisads-button type="primary" @click="handleAdd">
            {{ st('settingsWhitelist.btnAddPublisher') }}
          </artmisads-button>
        </div>
        <div class="table-panel">
          <artmisads-table
            :columns="columns"
            :data-source="dataSource"
            :loading="tableLoading"
            :row-key="record => record.id"
            :scroll="{ y: tableScrollHeight }"
            :pagination="{
              total: pagination.total,
              pageSize: pagination.size,
              current: pagination.page,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
            }"
            :change="onChangePage"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'publisher'">
                <div class="publisher-info">
                  <LogoImage
                    :logoUrl="record.avatarUrl"
                    :brandName="record.publisherName"
                    :width="40"
                    :height="40"
                    :fontSize="10"
                  />
                  <AutoTooltip :content="record.publisherName || '--'" :width="160" />
                </div>
              </template>
              <template v-if="column.key === 'bio'">
                <AutoTooltip
                  :content="record.bio || '--'"
                  :contentWidth="500"
                  :width="200"
                />
              </template>
              <template v-if="column.key === 'promotionMethods'">
                <AutoTooltip
                  :content="formatNames(record.promotionMethods)"
                  :width="160"
                />
              </template>
              <template v-if="column.key === 'promotionProductCategories'">
                <AutoTooltip
                  :content="formatNames(record.promotionCategories)"
                  :width="160"
                />
              </template>
              <template v-if="column.key === 'addTime'">
                {{ formatAddTime(record.createdAt) }}
              </template>
              <template v-if="column.key === 'action'">
                <artmisads-select-button  class="remove-link" @click="handleRemove(record)">
                  {{ st('settingsWhitelist.btnRemove') }}
                </artmisads-select-button>
              </template>
            </template>
          </artmisads-table>
        </div>
      </div>
    </a-spin>
    <AddPublisherModal ref="addPublisherModalRef" @refresh="refreshList" />
    <CopyToMarketplacesModal ref="copyToMarketplacesModalRef" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Modal, message } from 'ant-design-vue'
import moment from 'moment'
import breadcrumbs from '@/components/breadcrumbs.vue'
import LogoImage from '@/components/LogoImage.vue'
import AutoTooltip from '@/components/AutoTooltip.vue'
import NoticeTool from './components/noticeTool.vue'
import AddPublisherModal from './components/addPublisherModal.vue'
import CopyToMarketplacesModal from './components/copyToMarketplacesModal.vue'
import { whitelistApi } from '@/server/seller/index'
import { useSellerI18n } from '@/composables/useSellerI18n'
import { useTableScrollHeight } from '@/utils/useTableScrollHeight'
import { useUserInfoStore } from '@/storeData/index'
import { COUNTRY_TIME_TZ } from '~/contants/common.js'
const store = useUserInfoStore()
const timezone = COUNTRY_TIME_TZ[store.userInfo.countryCode]
const { st } = useSellerI18n()
const addPublisherModalRef = ref(null)
const copyToMarketplacesModalRef = ref(null)
const pageLoading = ref(false)
const tableLoading = ref(false)
const statusLoading = ref(false)
const whitelistEnabled = ref(0)
const dataSource = ref([])
const pagination = ref({
  total: 0,
  page: 1,
  size: 10,
})

const { tableScrollHeight } = useTableScrollHeight({
  selector: '.table-panel',
  bottomSpace: 155,
  minHeight: 200,
  maxHeight: 800,
  defaultHeight: 350,
})

const breadcrumbsData = computed(() => ({
  title: st('settingsWhitelist.pageTitle'),
  links: [
    { name: st('common.home'), path: '/seller/dashboard' },
    { name: st('settingsConnections.breadcrumbSettings'), path: '/seller/connection' },
    { name: st('settingsWhitelist.pageTitle'), path: '/seller/whitelist' },
  ],
}))

const statusText = computed(() =>
  whitelistEnabled.value
    ? st('settingsWhitelist.statusOpen')
    : st('settingsWhitelist.statusClosed')
)

const columns = computed(() => [
  {
    title: st('settingsWhitelist.colPublisher'),
    key: 'publisher',
    dataIndex: 'publisher',
    width: 220,
  },
  {
    title: st('settingsWhitelist.colBio'),
    key: 'bio',
    dataIndex: 'bio',
    width: 220,
  },
  {
    title: st('settingsWhitelist.colPromotionMethod'),
    key: 'promotionMethods',
    dataIndex: 'promotionMethods',
    width: 180,
  },
  {
    title: st('settingsWhitelist.colPromotionCategory'),
    key: 'promotionProductCategories',
    dataIndex: 'promotionProductCategories',
    width: 180,
  },
  {
    title: st('settingsWhitelist.colAddTime'),
    key: 'addTime',
    dataIndex: 'addTime',
    width: 140,
  },
  {
    title: st('settingsWhitelist.colAction'),
    key: 'action',
    dataIndex: 'action',
    width: 120,
    fixed: 'right',
  },
])

const formatNames = items => {
  if (!items?.length) return '--'
  if (typeof items[0] === 'string') return items.join(', ')
  return items.map(item => item.name).join(', ')
}

const formatAddTime = time => {
  if (!time) return '--'
  return moment(Number(time)).tz(timezone).format('YYYY-MM-DD')
}

const normalizeRecord = record => ({
  ...record,
  id: record.id,
  creatorName: record.creatorName ?? record.publisherVO?.creatorName,
  publicImage: record.publicImage ?? record.publisherVO?.publicImage,
  publicBio: record.publicBio ?? record.publisherVO?.publicBio,
  promotionMethods: record.promotionMethods ?? record.publisherVO?.promotionMethods,
  promotionProductCategories:
    record.promotionProductCategories ?? record.publisherVO?.promotionProductCategories,
  addTime: record.addTime ?? record.createdAt ?? record.createTime,
})

const getStatus = async () => {
  const res = await whitelistApi.getWhitelistStatus()
  if (res.success) {
    const data = res.data ?? res
    whitelistEnabled.value = data.whitelistEnabled===1?true:false
  }
}

const getList = async () => {
  tableLoading.value = true
  try {
    const res = await whitelistApi.getWhitelistList({
      page: pagination.value.page,
      size: pagination.value.size,
    })
    if (res.success) {
      const list = res.data?.data ?? res.data ?? []
      dataSource.value = list.map(normalizeRecord)
      pagination.value.total = res.data?.total ?? list.length
    }
  } finally {
    tableLoading.value = false
  }
}

const onStatusChange = async checked => {
  statusLoading.value = true
  const previous = whitelistEnabled.value
  const checkedStatus = checked ? 1 : 0
  try {
    const res = await whitelistApi.updateWhitelistStatus({ whitelistEnabled: checkedStatus })
    if (res.success) {
      message.success(st('settingsWhitelist.statusUpdateSuccess'))
      getStatus()
    } 
  } catch {
    whitelistEnabled.value = previous
  } finally {
    statusLoading.value = false
  }
}

const onChangePage = ({ current, pageSize }) => {
  pagination.value.page = current
  pagination.value.size = pageSize
  getList()
}

const handleAdd = () => {
  addPublisherModalRef.value?.open()
}

const handleCopy = () => {
  copyToMarketplacesModalRef.value?.open(pagination.value.total)
}

const handleRemove = record => {
  Modal.confirm({
    title: st('settingsWhitelist.removeConfirmTitle'),
    okText: st('settingsWhitelist.btnConfirm'),
    cancelText: st('settingsWhitelist.btnCancel'),
    onOk: async () => {
      const res = await whitelistApi.deleteWhitelistPublisher({ publisherId: record.publisherId })
      if (res.success) {
        message.success(st('settingsWhitelist.removeSuccess'))
        getList()
      } else {
        message.error(res.error)
      }
    },
  })
}

const refreshList = () => {
  pagination.value.page = 1
  getList()
}

const initPage = async () => {
  pageLoading.value = true
  try {
    await Promise.all([getStatus(), getList()])
  } finally {
    pageLoading.value = false
  }
}

onMounted(() => {
  initPage()
})
</script>

<style scoped lang="less">
.whitelist {
  padding: 16px;
  padding-top: unset;
  width: 100%;

  .content-wrapper {
    display: block !important;
    background-color: #fff;
    padding: 16px;
    border-radius: 8px;
  }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 12px;
  }

  .status-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .status-label {
    font-size: 14px;
    font-weight: 600;
    color: #0f0629;
    letter-spacing: 0.3px;
  }

  .status-value {
    margin-left: 4px;
  }

  .table-panel {
    border-radius: 8px;
    overflow: hidden;
  }

  .publisher-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  // .remove-link {
  //   color: var(--primary-text);
  //   font-weight: 500;
  //   cursor: pointer;

  //   &:hover {
  //     color: var(--primary-hover);
  //   }
  // }
}
</style>
